/**
 * Helpers for a ticket type's `quantityRule` — a string describing which
 * quantities a customer may buy. The rule is a comma-separated list of tokens,
 * where each token is either a single number (`3`) or an inclusive range
 * (`3-5`). For example `"0,1,3-5"` allows the quantities 0, 1, 3, 4 and 5.
 */

/**
 * Parses a quantity rule into the sorted, de-duplicated list of allowed
 * quantities. Whitespace is ignored and reversed ranges (`5-3`) are accepted.
 * Tokens that are neither a number nor a range are skipped.
 *
 * Returns `null` when the rule is absent or contains no valid quantities,
 * meaning "no restriction".
 */
export function parseQuantityRule(rule?: string | null): number[] | null {
  if (rule == null) return null;
  const trimmed = rule.trim();
  if (trimmed === '') return null;

  const allowed = new Set<number>();
  for (const rawToken of trimmed.split(',')) {
    const token = rawToken.trim();
    if (token === '') continue;

    const range = token.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const start = Number(range[1]);
      const end = Number(range[2]);
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);
      for (let n = lo; n <= hi; n++) allowed.add(n);
      continue;
    }

    if (/^\d+$/.test(token)) allowed.add(Number(token));
  }

  if (allowed.size === 0) return null;
  return Array.from(allowed).sort((a, b) => a - b);
}

/**
 * Whether `quantity` satisfies the rule. A `null` rule (no restriction) allows
 * any quantity.
 */
export function isQuantityAllowed(
  quantity: number,
  allowed: number[] | null
): boolean {
  if (allowed == null) return true;
  return allowed.includes(quantity);
}

/**
 * The next allowed quantity strictly greater than `current`, or `null` when
 * `current` already sits at the highest allowed value. With no rule the value
 * simply increases by one.
 */
export function nextAllowedQuantity(
  current: number,
  allowed: number[] | null
): number | null {
  if (allowed == null) return current + 1;
  return allowed.find((q) => q > current) ?? null;
}

/**
 * The previous allowed quantity strictly less than `current`, or `null` when
 * `current` already sits at the lowest allowed value (the rule's minimum). With
 * no rule the value simply decreases by one.
 */
export function previousAllowedQuantity(
  current: number,
  allowed: number[] | null
): number | null {
  if (allowed == null) return current - 1;
  const below = allowed.filter((q) => q < current);
  return below.length ? Math.max(...below) : null;
}

/**
 * Formats a parsed allowed-quantity list for display, compacting runs of three
 * or more consecutive values into a range. For example `[0, 1, 3, 4, 5]`
 * becomes `"0, 1, 3-5"` and `[2, 4, 6, 8]` stays `"2, 4, 6, 8"`.
 */
export function formatAllowedQuantities(allowed: number[]): string {
  const parts: string[] = [];
  let i = 0;
  while (i < allowed.length) {
    let j = i;
    while (j + 1 < allowed.length && allowed[j + 1] === allowed[j]! + 1) j++;
    if (j - i >= 2) parts.push(`${allowed[i]}-${allowed[j]}`);
    else for (let k = i; k <= j; k++) parts.push(`${allowed[k]}`);
    i = j + 1;
  }
  return parts.join(', ');
}

/**
 * How many seats one "add" action has to take to land on an allowed quantity,
 * starting from `current`. With no rule that is always one seat. A rule with
 * gaps (`0,2,4,6`) needs the whole step, so adding a seat at 2 takes two.
 *
 * Returns `null` when `current` already sits at the highest allowed value —
 * nothing can be added.
 */
export function seatsToAdd(
  current: number,
  allowed: number[] | null
): number | null {
  const next = nextAllowedQuantity(current, allowed);
  if (next == null) return null;
  return next - current;
}

/**
 * How many seats one "remove" action has to release to land on an allowed
 * quantity, starting from `current`. With no rule that is always one seat; a
 * gapped rule steps down by the whole gap. Below the rule's minimum there is no
 * lower allowed value, so the ticket type empties — a count of 0 is always
 * acceptable, the same way changing a seat's ticket type may empty its old one.
 */
export function seatsToRemove(
  current: number,
  allowed: number[] | null
): number {
  const previous = previousAllowedQuantity(current, allowed);
  return current - Math.max(previous ?? 0, 0);
}
